import type { TFunction } from 'i18next';
import type { AssessmentResultItemDataFragment } from '../../../Trainee/TraineeReports.api';
import type { CognitiveFitness, SimpleMetric } from '../../model';
import type { DigitSpanResult, DigitSpanVariantSummary } from '@/apolloClient.types';
import { DigitSpanVariant } from '@/apolloClient.types';

const getDigitSpanByVariant = (
    result: DigitSpanResult,
    variant: DigitSpanVariant
): DigitSpanVariantSummary | undefined => {
    const variantSummaries = result.variantSummaries || [];
    return variantSummaries.find(v => v.variant === variant);
};

const parseDigitSpan = (t: TFunction, items: AssessmentResultItemDataFragment[]): CognitiveFitness | null => {
    const item = items.find(i => i.result.__typename === 'DigitSpanResult');
    const mi = item?.moduleInstance;
    const r = item?.result as DigitSpanResult;
    if (!mi?.name || !r) {
        return null;
    }

    const metrics: SimpleMetric[] = [];
    const forwardSummary = getDigitSpanByVariant(r, DigitSpanVariant.Forward);
    if (forwardSummary) {
        metrics.push({
            label: t('traineeReports:performance.cognitiveFitness.maxForwardSpanScore'),
            value: forwardSummary.spanScore ?? 0,
        });
        metrics.push({
            label: t('traineeReports:performance.cognitiveFitness.forwardCorrectTrials'),
            value: forwardSummary.totalCorrectTrials ?? 0,
        });
    }
    const backwardSummary = getDigitSpanByVariant(r, DigitSpanVariant.Backward);
    if (backwardSummary) {
        metrics.push({
            label: t('traineeReports:performance.cognitiveFitness.maxBackwardSpanScore'),
            value: backwardSummary.spanScore ?? 0,
        });
        metrics.push({
            label: t('traineeReports:performance.cognitiveFitness.backwardCorrectTrials'),
            value: backwardSummary.totalCorrectTrials ?? 0,
        });
    }

    return {
        name: mi.name ?? '',
        moduleDefinitionIdentifier: mi.moduleDefinitionIdentifier ?? '',
        description: mi.description ?? '',
        result: metrics,
    };
};

export default parseDigitSpan;
